// Cloudflare Pages Functions API 客户端
// 统一使用完整 URL（https://nzx-5o4.pages.dev/api/...）
//
// 在 Android APK 环境下，优先使用 NativeBridge 的原生 HTTP 方法（postJson/getJson）
// 原生 HTTP 完全绕过 WebView CORS 限制，稳定可靠
// 网页版 fallback 到 XMLHttpRequest

// 🔴 直接用完整域名，不再纠结相对路径 vs 绝对路径
// Cloudflare Pages Functions 在同一域名下，所以也不会有 CORS 问题
// 关键：无论网页还是 APK，都用同一个完整 URL
const API_BASE = '/api';

export interface Video {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  videoUrl: string;
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: string;
  views: number;
  tags?: string[];
}

export interface Comment {
  id: string;
  videoId: string;
  username: string;
  content: string;
  createdAt: string;
  replyTo?: string;
  replyToUsername?: string;
}

export interface User {
  id: string;
  username: string;
  password: string;
  role: 'admin' | 'user';
  createdAt: string;
  isOnline: boolean;
  lastSeen: string;
}

// ====================================================================
// 环境检测
// ====================================================================
function isAndroidNative(): boolean {
  try {
    const nb = (window as any).NativeBridge;
    return !!(nb && typeof nb.postJson === 'function');
  } catch (e) {}
  return false;
}

function hasNativeUpload(): boolean {
  try {
    const nb = (window as any).NativeBridge;
    return typeof nb?.uploadChunkBase64 === 'function';
  } catch (e) { return false; }
}

// ====================================================================
// URL 构造 —— 统一的路径处理
// 网页模式：相对路径 /api/xxx（自动用当前域名，无 CORS
// APK 模式：需要完整 URL，因为 Java 原生 HTTP 不知道 origin
// ====================================================================
const APK_BASE_HOST_KEY = 'nzx_api_host'; // localStorage 中存的远程 API host

function getApiOriginForNative(): string {
  // APK 环境下用硬编码的远程域名（部署到哪个 Pages 项目就填哪个
  // 也允许在 localStorage 设置自定义 host 便于测试
  const custom = (() => {
    try { return (window as any).localStorage.getItem(APK_BASE_HOST_KEY); } catch { return null; }
  })();
  return custom || 'https://nzx-5o4.pages.dev';
}

function buildApiUrl(path: string): string {
  if (isAndroidNative()) {
    // APK：完整 URL 给 Java 原生 HTTP
    return getApiOriginForNative() + '/api' + path
      + (path.includes('?') ? '&' : '?') + 't=' + Date.now() + '&r=' + Math.random().toString(36).slice(2, 6);
  }
  // 网页：相对路径，自动用当前 origin，零 CORS 问题
  return '/api' + path
    + (path.includes('?') ? '&' : '?') + 't=' + Date.now() + '&r=' + Math.random().toString(36).slice(2, 6);
}

// ====================================================================
// HTTP 核心
//   Android APK：用 NativeBridge 原生 HTTP（完全绕过 WebView CORS）
//   网页：XMLHttpRequest（用当前域名的相对路径，零 CORS 问题）
// ====================================================================
function httpNativePost(path: string, bodyObj: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    try {
      const nb = (window as any).NativeBridge;
      const url = buildApiUrl(path);
      const jsonBody = typeof bodyObj === 'string' ? bodyObj : JSON.stringify(bodyObj);
      console.log(`[HTTP] [APK] 原生 POST ${url}`);
      const resultStr = nb.postJson(url, jsonBody);
      console.log(`[HTTP] [APK] POST 返回长度=${resultStr ? resultStr.length : 0}`);
      const result = JSON.parse(resultStr || '{}');
      if (result.success === false) {
        reject(new Error(result.message || '原生请求失败'));
      } else if (result.data !== undefined) {
        resolve(result.data);
      } else {
        resolve(result);
      }
    } catch (e: any) {
      console.error(`[HTTP] [APK] POST 异常:`, e.message);
      reject(e);
    }
  });
}

function httpNativeGet(path: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    try {
      const nb = (window as any).NativeBridge;
      const url = buildApiUrl(path);
      console.log(`[HTTP] [APK] 原生 GET ${url}`);
      const resultStr = nb.getJson(url);
      console.log(`[HTTP] [APK] GET 返回长度=${resultStr ? resultStr.length : 0}`);
      const result = JSON.parse(resultStr || '{}');
      if (result.success === false) {
        reject(new Error(result.message || '原生请求失败'));
      } else if (result.data !== undefined) {
        resolve(result.data);
      } else {
        resolve(result);
      }
    } catch (e: any) {
      console.error(`[HTTP] [APK] GET 异常:`, e.message);
      reject(e);
    }
  });
}

// 网页模式：使用 fetch API
function httpXhr(path: string, options: { method?: string; body?: any } = {}): Promise<any> {
  const url = buildApiUrl(path);
  const method = options.method || 'GET';

  const fetchOptions: RequestInit = {
    method,
    headers: {
      'Accept': 'application/json',
    },
    credentials: 'omit',
  };

  if (options.body && method !== 'GET') {
    if (typeof options.body === 'string') {
      fetchOptions.body = options.body;
      (fetchOptions.headers as Record<string, string>)['Content-Type'] = 'application/json';
    } else {
      fetchOptions.body = JSON.stringify(options.body);
      (fetchOptions.headers as Record<string, string>)['Content-Type'] = 'application/json';
    }
  }

  console.log(`[HTTP] [Web] ${method} ${url}`);

  return fetch(url, fetchOptions)
    .then(response => {
      console.log(`[HTTP] [Web] 响应 status=${response.status}`);
      if (!response.ok) {
        return response.text().then(text => {
          throw new Error(`HTTP ${response.status}: ${text.substring(0, 200)}`);
        });
      }
      return response.json().catch(() => response.text());
    })
    .catch(error => {
      console.error(`[HTTP] [Web] ❌ 请求失败:`, error.message);
      throw error;
    });
}

function http(path: string, options: { method?: string; body?: any } = {}): Promise<any> {
  const method = (options.method || 'GET').toUpperCase();
  const useNative = isAndroidNative();
  console.log(`[HTTP] ${method} ${path}, 原生HTTP: ${useNative}`);

  if (useNative) {
    if (method === 'GET') return httpNativeGet(path);
    if (method === 'POST' || method === 'PUT' || method === 'DELETE') return httpNativePost(path, options.body);
  }

  console.log(`[HTTP] 使用 Web HTTP ${method}`);
  return httpXhr(path, options);
}

// ====================================================================
// 视频相关
// ====================================================================
export const videoService = {
  getAll: async (): Promise<Video[]> => http('/videos'),
  getById: async (id: string): Promise<Video | undefined> => http(`/videos/${encodeURIComponent(id)}`),
  getByUser: async (username: string): Promise<Video[]> => http(`/videos?byUser=${encodeURIComponent(username)}`),
  add: async (video: Omit<Video, 'id' | 'uploadedAt' | 'views'> & { tags?: string[] }): Promise<Video> =>
    http('/videos', { method: 'POST', body: video }),
  delete: async (id: string): Promise<void> => http(`/videos/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  deleteMultiple: async (ids: string[]): Promise<void> =>
    http('/videos/batch-delete', { method: 'POST', body: { ids } }),
  incrementViews: async (id: string): Promise<void> =>
    http(`/videos/${encodeURIComponent(id)}/views`, { method: 'POST' }),
};

// ====================================================================
// 评论相关
// ====================================================================
export const commentService = {
  getAll: async (): Promise<Comment[]> => [],
  getByVideoId: async (videoId: string): Promise<Comment[]> => http(`/videos/${encodeURIComponent(videoId)}/comments`),
  add: async (comment: Omit<Comment, 'id' | 'createdAt'>): Promise<Comment> =>
    http('/comments', { method: 'POST', body: comment }),
  delete: async (id: string): Promise<void> => http(`/comments/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// ====================================================================
// 用户相关
// ====================================================================
export const userService = {
  getAll: async (): Promise<User[]> => http('/users'),
  getAllIncludingAdmin: async (): Promise<User[]> => http('/users?includeAdmin=1'),
  getById: async (id: string): Promise<User | undefined> => http(`/users/${encodeURIComponent(id)}`),
  authenticate: async (username: string, password: string): Promise<User | null> => {
    try { return await http('/auth', { method: 'POST', body: { username, password } }); }
    catch (e) { return null; }
  },
  add: async (user: Omit<User, 'id' | 'createdAt' | 'isOnline' | 'lastSeen'>): Promise<User> =>
    http('/users', { method: 'POST', body: user }),
  delete: async (id: string): Promise<void> => http(`/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  update: async (id: string, update: { username?: string; password?: string; role?: 'admin' | 'user' }): Promise<User> =>
    http(`/users/${encodeURIComponent(id)}`, { method: 'PUT', body: update }),
  updateOnlineStatus: async (id: string, isOnline: boolean): Promise<void> =>
    http(isOnline ? `/users/${encodeURIComponent(id)}/activity` : `/users/${encodeURIComponent(id)}/logout`, { method: 'POST' }),
  getUserVideoCount: async (username: string): Promise<number> => {
    const r = await http(`/users/${encodeURIComponent(username)}/video-count`);
    return (r && typeof r.count === 'number') ? r.count : 0;
  },
};

// ====================================================================
// 在线用户
// ====================================================================
export const onlineService = {
  getOnlineCount: async (): Promise<number> => {
    try {
      const users: User[] = await http('/users');
      return users.filter(u => !!u.isOnline).length;
    } catch { return 0; }
  },
  updateActivity: async (userId: string): Promise<void> => userService.updateOnlineStatus(userId, true),
  removeUser: async (userId: string): Promise<void> => userService.updateOnlineStatus(userId, false),
};

// ====================================================================
// 文件存储（大文件上传）
// ====================================================================
//
// 上传流程：
//   1. 调用 /api/upload/init 创建会话，得到 uploadId
//   2. 将文件切分为多个 chunk（5MB一片，让进度条更灵敏）
//   3. 逐块读取文件（避免一次性读入内存）
//   4. 上传分片（带细粒度进度 + 重试机制）
//   5. 全部完成后调用 /api/upload/complete 合并，得到最终 URL
// ====================================================================

// ============================================================
// 文件上传（分片上传 + 实时进度 + 重试机制）
//
// 🔴 关键：根据环境自动选择上传方式
//   - APK 模式（有 NativeBridge）：用原生 HTTP，绕过 WebView CORS
//   - 网页模式：用 XMLHttpRequest multipart/form-data
// ============================================================

// 分片大小：20MB（APK 模式用）
const MAX_CHUNK_SIZE = 20 * 1024 * 1024;
// 单个分片重试次数
const MAX_RETRY = 5;
// 重试间隔
const RETRY_DELAY = 1500;

export const fileService = {
  saveVideo: async (file: File, onProgress?: (percent: number) => void): Promise<string> => {
    console.log(`[Upload] 🚀 开始上传视频: ${file.name}, ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    return uploadFile(file, onProgress);
  },
  saveThumbnail: async (file: File, onProgress?: (percent: number) => void): Promise<string> => {
    console.log(`[Upload] 🖼️ 开始上传缩略图: ${file.name}, ${(file.size / 1024).toFixed(2)}KB`);
    const formData = new FormData();
    formData.append('file', file, file.name);
    const resp = await sendFormDataWeb('/upload/form', formData, onProgress);
    if (!resp.url) throw new Error('缩略图上传失败：服务端未返回 URL');
    return resp.url;
  },
};

// 🔴 大文件上传：同源分片上传，Functions 做"透明字节管道"
// 核心设计：
// 1. 把文件切成 8MB 小分片（小到 Functions 不会超时/限流）
// 2. 每个分片用 XHR.send(blob) 以纯二进制 PUT 到 /api/upload/chunk?uploadId=xxx&chunkIndex=n
// 3. 后端不解析 body，直接 request.body → R2，CPU 开销 ≈ 0
// 4. 每片独立重试（指数退避），整体进度 = 已完成片数/总片数
// 5. 完成后调用 /api/upload/complete 让后端流式合并
async function uploadFile(file: File, onProgress?: (percent: number) => void): Promise<string> {
  const MAX_RETRY = 5;
  const RETRY_BASE_DELAY = 1500;
  // 🔴 8MB/片 —— 小而快，Cloudflare Functions 可以在几毫秒内完成，
  //    避免大分片长时间占用 CPU 导致 503。大文件（260MB）就是 ~33 片。
  const CHUNK_SIZE = 8 * 1024 * 1024;

  const totalFileSize = file.size;
  const totalChunks = Math.max(1, Math.ceil(totalFileSize / CHUNK_SIZE));
  const sizeMB = (totalFileSize / 1024 / 1024).toFixed(2);

  console.log(`[Upload] ========== 分片上传（8MB/片）开始 ==========`);
  console.log(`[Upload] 文件: ${file.name}, ${sizeMB}MB, 分片数: ${totalChunks}`);

  if (onProgress) onProgress(1);

  // 步骤 1：调用 init（带 query 参数）获取 uploadId
  const initQuery = new URLSearchParams({
    filename: file.name || `upload-${Date.now()}.bin`,
    totalChunks: String(totalChunks),
    chunkSize: String(CHUNK_SIZE),
  }).toString();

  let uploadId = '';
  let safeFilename = '';
  for (let attempt = 0; attempt < MAX_RETRY && !uploadId; attempt++) {
    try {
      console.log(`[Upload] init 第 ${attempt + 1}/${MAX_RETRY} 次...`);
      const resp = await fetch(API_BASE + `/upload/init?${initQuery}&t=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      uploadId = data.uploadId;
      safeFilename = data.filename || safeFilename;
    } catch (e: any) {
      console.error(`[Upload] ❌ init 失败:`, e.message);
      if (attempt < MAX_RETRY - 1) {
        await new Promise((r) => setTimeout(r, RETRY_BASE_DELAY + Math.random() * 1500));
      } else {
        throw e;
      }
    }
  }
  if (!uploadId) throw new Error('创建上传会话失败');
  console.log(`[Upload] ✅ 会话创建成功 uploadId=${uploadId}`);
  if (onProgress) onProgress(3);

  // 步骤 2：逐个分片上传 —— XHR 纯二进制 PUT 到 /api/upload/chunk?uploadId=xxx&chunkIndex=n
  let lastErr: any = null;
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalFileSize);
    const blob = file.slice(start, end);

    let chunkOk = false;
    const chunkBaseProgress = 3 + (i / totalChunks) * 95; // 3%~98% 区间
    const chunkProgressSpan = (1 / totalChunks) * 95;

    for (let attempt = 0; attempt < MAX_RETRY && !chunkOk; attempt++) {
      console.log(`[Upload] 分片 ${i + 1}/${totalChunks} 第 ${attempt + 1} 次尝试 (${(blob.size / 1024 / 1024).toFixed(2)}MB)`);
      const tryDone = await new Promise<boolean>((resolve) => {
        const xhr = new XMLHttpRequest();
        // 同源 URL，零 CORS 问题。query 参数传递 metadata，body 纯二进制
        const chunkUrl = API_BASE + `/upload/chunk?uploadId=${encodeURIComponent(uploadId)}&chunkIndex=${i}&t=${Date.now()}`;
        xhr.open('PUT', chunkUrl, true);

        // 小分片超时 90 秒就够（8MB 在差网络下也最多几十秒）
        xhr.timeout = 90 * 1000;

        if (xhr.upload && onProgress) {
          xhr.upload.onprogress = (evt) => {
            if (evt.lengthComputable && evt.total > 0) {
              const chunkPct = evt.loaded / evt.total; // 0~1
              onProgress(Math.round(chunkBaseProgress + chunkPct * chunkProgressSpan));
            }
          };
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log(`[Upload] ✅ 分片 ${i + 1}/${totalChunks} 完成`);
            resolve(true);
          } else {
            const txt = xhr.responseText || '';
            console.error(`[Upload] ❌ 分片 ${i + 1} 失败 HTTP ${xhr.status}: ${txt.slice(0, 200)}`);
            lastErr = new Error(`HTTP ${xhr.status}`);
            resolve(false);
          }
        };

        xhr.onerror = () => {
          console.error(`[Upload] ❌ 分片 ${i + 1} onerror（网络问题）`);
          lastErr = new Error('网络错误');
          resolve(false);
        };

        xhr.ontimeout = () => {
          console.error(`[Upload] ❌ 分片 ${i + 1} timeout`);
          lastErr = new Error('分片超时');
          resolve(false);
        };

        // 纯二进制 body，不经过 FormData 解析
        xhr.send(blob);
      });

      chunkOk = tryDone;

      if (!chunkOk && attempt < MAX_RETRY - 1) {
        // 失败：指数退避 + 随机抖动
        const delay = RETRY_BASE_DELAY * Math.pow(2, attempt) + Math.random() * 2000;
        console.log(`[Upload] ⏳ ${(delay / 1000).toFixed(1)}s 后重试分片 ${i + 1}...`);
        await new Promise((r) => setTimeout(r, delay));
      }
    }

    if (!chunkOk) throw lastErr || new Error(`分片 ${i + 1} 上传失败`);

    // 片间微小间隔（给 Cloudflare 一点喘息，避免连续请求触发限流）
    if (i < totalChunks - 1) {
      await new Promise((r) => setTimeout(r, 300 + Math.random() * 400));
    }
  }

  // 步骤 3：调用 complete 合并
  console.log(`[Upload] 步骤 3: 请求合并分片...`);
  if (onProgress) onProgress(99);

  const completeQuery = new URLSearchParams({
    uploadId,
    filename: safeFilename || (file.name || `${Date.now()}.bin`),
    totalChunks: String(totalChunks),
  }).toString();

  let finalUrl = '';
  for (let attempt = 0; attempt < MAX_RETRY && !finalUrl; attempt++) {
    try {
      // complete 请求通常需要几到十几秒（R2 读取分片再写入）
      // 给 5 分钟超时 + AbortController
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000);
      try {
        const resp = await fetch(API_BASE + `/upload/complete?${completeQuery}&t=${Date.now()}`, {
          method: 'POST',
          signal: controller.signal,
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        finalUrl = data.url || '';
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (e: any) {
      console.error(`[Upload] ❌ complete 失败 (第 ${attempt + 1} 次):`, e.message);
      if (attempt < MAX_RETRY - 1) {
        await new Promise((r) => setTimeout(r, RETRY_BASE_DELAY * Math.pow(2, attempt)));
      } else {
        throw e;
      }
    }
  }

  if (!finalUrl) throw new Error('合并失败：未返回 URL');

  if (onProgress) onProgress(100);
  console.log(`[Upload] ✅ 全部完成: ${finalUrl}`);
  return finalUrl;
}

// 简单的重试包装
async function retryRequest<T>(fn: () => Promise<T>, maxRetry: number): Promise<T> {
  let lastErr: any = null;
  for (let i = 0; i < maxRetry; i++) {
    try {
      return await fn();
    } catch (e: any) {
      lastErr = e;
      if (i < maxRetry - 1) {
        await new Promise((r) => setTimeout(r, 2000));
      }
    }
  }
  throw lastErr || new Error('请求失败');
}

// 辅助：JSON POST（带重试 + 双重超时保护）
// 🔴 统一走 XMLHttpRequest，不分 APK/网页，避免 NativeBridge 兼容性问题
// 🔴 双重超时：XHR 本身的 timeout + 额外的 setTimeout 强制超时（应对 WebView 兼容性）
async function postJsonWithRetry(path: string, body: any, maxRetry: number): Promise<any> {
  const tryOnce = (): Promise<any> => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = API_BASE + path + '?t=' + Date.now() + '&r=' + Math.random().toString(36).slice(2, 6);
    console.log(`[Upload] POST ${url}`);

    const FORCE_TIMEOUT = 40000; // 40秒强制超时（比 XHR timeout 多10秒，确保能触发）
    const forceTimeoutTimer = setTimeout(() => {
      console.error(`[Upload] POST ${path} ⚠️ 强制超时（${FORCE_TIMEOUT}ms）`);
      xhr.abort();
      reject(new Error('请求超时'));
    }, FORCE_TIMEOUT);

    xhr.open('POST', url, true);
    xhr.timeout = 30000;
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');

    xhr.onload = () => {
      clearTimeout(forceTimeoutTimer);
      console.log(`[Upload] POST ${path} HTTP ${xhr.status}, body=${xhr.responseText.slice(0, 200)}`);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve(data);
        } catch {
          resolve({});
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      clearTimeout(forceTimeoutTimer);
      console.error(`[Upload] POST ${path} onerror —— 网络错误`);
      reject(new Error('网络错误'));
    };

    xhr.ontimeout = () => {
      clearTimeout(forceTimeoutTimer);
      console.error(`[Upload] POST ${path} timeout —— 30秒未完成`);
      reject(new Error('请求超时'));
    };

    xhr.onabort = () => {
      clearTimeout(forceTimeoutTimer);
      console.error(`[Upload] POST ${path} aborted`);
    };

    xhr.send(JSON.stringify(body));
  });

  let lastErr: any = null;
  for (let i = 0; i < maxRetry; i++) {
    try {
      return await tryOnce();
    } catch (e: any) {
      lastErr = e;
      console.log(`[Upload] POST ${path} 第${i + 1}/${maxRetry}次尝试失败:`, e.message);
      if (i < maxRetry - 1) {
        await new Promise((r) => setTimeout(r, 2000));
      }
    }
  }
  throw lastErr || new Error('请求失败');
}

// ============================================================
// 底层 HTTP 工具：自动选择 APK/网页模式
// ============================================================

// 统一 JSON POST：APK 用 NativeBridge.postJson，网页用 XHR
function postJson(path: string, body: any): Promise<any> {
  const useNative = isAndroidNative();
  console.log(`[Upload HTTP] POST ${path} 模式: ${useNative ? 'APK(原生)' : '网页(XHR)'}`);

  if (useNative) {
    return new Promise((resolve, reject) => {
      try {
        const nb = (window as any).NativeBridge;
        const url = API_BASE + path + (path.includes('?') ? '&' : '?') + 't=' + Date.now();
        const jsonBody = JSON.stringify(body);
        console.log(`[Upload HTTP] [APK] 调用 nb.postJson(url=${url})`);
        const resultStr = nb.postJson(url, jsonBody);
        console.log(`[Upload HTTP] [APK] 返回: ${resultStr ? '长度=' + resultStr.length : 'null/空'}`);
        
        if (!resultStr) {
          reject(new Error('请求失败：服务端无响应'));
          return;
        }
        
        const result = JSON.parse(resultStr);
        console.log(`[Upload HTTP] [APK] JSON解析:`, JSON.stringify(result));
        
        // 🔴 兼容后端两种返回格式：
        // 格式1: { success: true, data: { uploadId } } （NativeBridge.postJson 包装后）
        // 格式2: { uploadId } （后端直接返回，无包装）
        if (result.success === false) {
          reject(new Error(result.message || '请求失败'));
        } else if (result.data) {
          // 格式1：有 data 字段
          resolve(result.data);
        } else {
          // 格式2：直接返回数据（如 { uploadId }）
          resolve(result);
        }
      } catch (e: any) {
        console.error(`[Upload HTTP] ❌ ${path} 原生异常:`, e.message);
        reject(new Error('网络请求失败：' + (e.message || '请检查网络')));
      }
    });
  } else {
    // 网页模式：直接复用 http() 函数（已经走 XHR）
    console.log(`[Upload HTTP] [Web] 使用 XHR POST ${path}`);
    return http(path, { method: 'POST', body });
  }
}

// FormData 上传 + 进度回调（分片专用）
// 🔴 统一走 XMLHttpRequest，不分 APK/网页，避免 NativeBridge 兼容性问题
function sendFormDataWeb(
  path: string,
  formData: FormData,
  onProgress?: (percent: number) => void
): Promise<any> {
  const xhr = new XMLHttpRequest();
  const url = API_BASE + path + '?t=' + Date.now() + '&r=' + Math.random().toString(36).slice(2, 6);
  console.log(`[Upload] FormData POST ${url}`);

  return new Promise((resolve, reject) => {
    xhr.open('POST', url, true);
    xhr.timeout = 5 * 60 * 1000; // 5 分钟超时（20MB 正常几十秒）
    xhr.setRequestHeader('Accept', 'application/json');

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && evt.total > 0) {
          const percent = Math.round((evt.loaded / evt.total) * 100);
          onProgress(percent);
        }
      };
    }

    xhr.onload = () => {
      console.log(`[Upload] FormData 响应 status=${xhr.status}, body=${xhr.responseText.slice(0, 200)}`);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data && data.error) {
            reject(new Error(data.error || data.message || '服务端错误'));
            return;
          }
          resolve(data);
        } catch {
          resolve({});
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      console.error(`[Upload] FormData onerror —— 网络/CORS 问题`);
      reject(new Error('网络错误'));
    };

    xhr.ontimeout = () => {
      console.error(`[Upload] FormData timeout —— 5 分钟未完成`);
      reject(new Error('上传超时，请重试'));
    };

    xhr.send(formData);
  });
}

// ArrayBuffer → base64（JS 端编码，然后传给 Java 原生上传）
function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;  // 32KB 一块，避免 apply 栈溢出
  let result = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    result += String.fromCharCode.apply(null, Array.from(slice) as number[]);
  }
  return btoa(result);
}

// ============================================================
// fetch + 超时控制（用于合并分片等可能耗时的请求）
// ============================================================
function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<any> {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
      reject(new Error('请求超时'));
    }, timeoutMs);

    fetch(url, { ...options, signal: controller.signal })
      .then(response => {
        clearTimeout(timeout);
        if (!response.ok) {
          return response.text().then(text => {
            reject(new Error(`HTTP ${response.status}: ${text.substring(0, 200)}`));
          });
        }
        return response.json();
      })
      .then(data => resolve(data))
      .catch(err => {
        clearTimeout(timeout);
        if (err.name === 'AbortError') {
          reject(new Error('请求超时'));
        } else {
          reject(err);
        }
      });
  });
}